import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { UsersThree, ArrowsClockwise, Moon, CalendarCheck } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { PageHead } from "./Properties";

const Kpi = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="rounded-2xl border border-border bg-white p-5">
    <div className="flex items-center gap-2 text-muted-foreground"><Icon size={18} className="text-[#D96C4A]" /><span className="label-xs">{label}</span></div>
    <p className="mt-3 font-display text-4xl" data-testid={testId}>{value}</p>
    {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
  </div>
);

const EMPTY = { months: [], checkins: 0, unique_guests: 0, returning_guests: 0, returning_rate: 0, total_guests: 0, nights: 0, heard: [] };

export default function Insights() {
  const [data, setData] = useState(null);
  const [props, setProps] = useState([]);
  const [scope, setScope] = useState("all");
  useEffect(() => {
    api.get("/insights").then((r) => setData(r.data));
    api.get("/properties").then((r) => setProps(r.data));
  }, []);
  if (!data) return null;
  const d = scope === "all" ? data.all : data.by_property[scope] || { ...EMPTY, months: data.all.months.map((m) => ({ ...m, checkins: 0, guests: 0 })) };
  const maxHeard = Math.max(1, ...d.heard.map((h) => h.count));

  return (
    <div data-testid="insights-page">
      <PageHead eyebrow="Insights" title="How your Airbnbs are doing">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56 bg-white" data-testid="insights-property-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Airbnbs</SelectItem>{props.map((p) => <SelectItem key={p.property_id} value={p.property_id}>{p.data.name}</SelectItem>)}</SelectContent>
        </Select>
      </PageHead>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={CalendarCheck} label="Check-ins" value={d.checkins} sub="all time" testId="insight-kpi-checkins" />
        <Kpi icon={UsersThree} label="Guests hosted" value={d.total_guests} sub={`${d.unique_guests} unique contacts`} testId="insight-kpi-guests" />
        <Kpi icon={ArrowsClockwise} label="Returning guests" value={`${d.returning_rate}%`} sub={`${d.returning_guests} of ${d.unique_guests} came back`} testId="insight-kpi-returning" />
        <Kpi icon={Moon} label="Nights booked" value={d.nights} sub={d.checkins ? `${(d.nights / d.checkins).toFixed(1)} nights per stay` : "—"} testId="insight-kpi-nights" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <section className="rounded-2xl border border-border bg-white p-6 lg:col-span-7" data-testid="insight-months-chart">
          <h2 className="font-display text-lg">Guests per month</h2>
          <p className="text-sm text-muted-foreground">Last 6 months, by check-in date</p>
          <div className="mt-4 h-72">
            <ResponsiveContainer>
              <BarChart data={d.months}>
                <CartesianGrid stroke="#E8E4DF" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} stroke="#6E6864" />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} stroke="#6E6864" width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DF" }} cursor={{ fill: "rgba(217,108,74,.06)" }} />
                <Legend iconType="circle" />
                <Bar dataKey="checkins" name="Check-ins" fill="#8A9A86" radius={[6, 6, 0, 0]} />
                <Bar dataKey="guests" name="Guests" fill="#D96C4A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-white p-6 lg:col-span-5" data-testid="insight-heard">
          <h2 className="font-display text-lg">Where guests heard about you</h2>
          <p className="text-sm text-muted-foreground">From the check-in form</p>
          <div className="mt-5 space-y-3">
            {d.heard.length === 0 && <p className="text-sm text-muted-foreground">No check-ins yet.</p>}
            {d.heard.map((h) => (
              <div key={h.source} data-testid={`heard-${h.source}`}>
                <div className="flex justify-between text-sm"><span className="font-medium">{h.source}</span><span className="text-muted-foreground">{h.count} · {Math.round((100 * h.count) / d.checkins)}%</span></div>
                <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-[#151312] transition-[width] duration-500" style={{ width: `${(100 * h.count) / maxHeard}%` }} /></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
